import React, { useState, useEffect } from 'react';
import { 
  Package, 
  AlertTriangle, 
  CheckCircle, 
  PackageOpen, 
  Link as LinkIcon,
  Truck,
  Calendar,
  Clock,
  ArrowRight,
  Loader2,
  RefreshCw
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { fetchFulfillmentOrders, fetchDeliveryOrders, fetchDashboardStats } from '../lib/api';
import type { Order } from '../lib/types';

const DashboardHome: React.FC = () => {
  const [stats, setStats] = useState({
    totalProducts: 0,
    lowStockProducts: 0,
    totalQoh: 0,
    activeOrderLinks: 0
  });

  const [fulfillmentQueue, setFulfillmentQueue] = useState<Order[]>([]);
  const [loadingFulfillment, setLoadingFulfillment] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [deliveryOrders, setDeliveryOrders] = useState<{
    pendingDeliveries: Order[];
    completedToday: number;
    scheduledTomorrow: number;
  }>({
    pendingDeliveries: [],
    completedToday: 0,
    scheduledTomorrow: 0
  });
  const [loadingDeliveries, setLoadingDeliveries] = useState(true);
  const [deliveryError, setDeliveryError] = useState<string | null>(null);

  useEffect(() => {
    loadFulfillmentQueue();
    loadDeliveryOrders();
    loadDashboardStats();
  }, []);

  const handleRefresh = async () => {
    if (refreshing) return;
    
    setRefreshing(true);
    try {
      await Promise.all([
        loadFulfillmentQueue(),
        loadDeliveryOrders(),
        loadDashboardStats()
      ]);
    } finally {
      setRefreshing(false);
    }
  };

  const loadDashboardStats = async () => {
    try {
      setLoadingStats(true);
      const data = await fetchDashboardStats();
      setStats(data);
      setError(null);
    } catch (err) {
      console.error('Error loading dashboard stats:', err);
      setError('Failed to load dashboard statistics');
    } finally {
      setLoadingStats(false);
    }
  };

  const loadFulfillmentQueue = async () => {
    try {
      setLoadingFulfillment(true);
      const orders = await fetchFulfillmentOrders();
      setFulfillmentQueue(orders);
      setError(null);
    } catch (err) {
      console.error('Error loading fulfillment queue:', err);
      setError('Failed to load fulfillment queue');
    } finally {
      setLoadingFulfillment(false);
    }
  };

  const loadDeliveryOrders = async () => {
    try {
      setLoadingDeliveries(true);
      const data = await fetchDeliveryOrders();
      setDeliveryOrders(data);
      setDeliveryError(null);
    } catch (err) {
      console.error('Error loading delivery orders:', err);
      setDeliveryError('Failed to load delivery orders');
    } finally {
      setLoadingDeliveries(false);
    }
  };

  const calculateProgress = (order: Order): number => {
    const totalItems = order.items.reduce((sum, item) => sum + item.quantity, 0);
    if (totalItems === 0) return 0;

    const fulfilledItems = order.items.reduce((sum, item) => sum + (item.quantityFulfilled || 0), 0);
    return Math.round((fulfilledItems / totalItems) * 100);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">Welcome to WASP Wholesale</h2>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center space-x-2 text-gray-600 hover:text-green-600 disabled:opacity-50"
        >
          <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          <span className="text-sm">Refresh</span>
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Products</p>
              {loadingStats ? (
                <div className="h-8 flex items-center">
                  <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                </div>
              ) : (
                <p className="text-2xl font-bold text-gray-900">{stats.totalProducts}</p>
              )}
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <Package className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Low Stock Products</p>
              {loadingStats ? (
                <div className="h-8 flex items-center">
                  <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                </div>
              ) : (
                <p className="text-2xl font-bold text-gray-900">{stats.lowStockProducts}</p>
              )}
            </div>
            <div className="bg-orange-100 p-3 rounded-full">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total QOH</p>
              {loadingStats ? (
                <div className="h-8 flex items-center">
                  <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                </div>
              ) : (
                <p className="text-2xl font-bold text-gray-900">{stats.totalQoh.toLocaleString()}</p>
              )}
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <PackageOpen className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Order Links</p>
              {loadingStats ? (
                <div className="h-8 flex items-center">
                  <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                </div>
              ) : (
                <p className="text-2xl font-bold text-gray-900">{stats.activeOrderLinks}</p>
              )}
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <LinkIcon className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Fulfillment Queue Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Fulfillment Queue</h3>
          <Link
            to="/dashboard/fulfillment"
            className="text-purple-600 hover:text-purple-700 text-sm font-medium flex items-center"
          >
            View All Orders
            <ArrowRight className="w-4 h-4 ml-1" />
          </Link>
        </div>

        <div className="space-y-4">
          {loadingFulfillment ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-600">
              {error}
              <button
                onClick={loadFulfillmentQueue}
                className="block mx-auto mt-2 text-sm text-purple-600 hover:text-purple-700"
              >
                Try Again
              </button>
            </div>
          ) : fulfillmentQueue.length > 0 ? (
            fulfillmentQueue.map((order) => (
              <div key={order.id} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-medium text-gray-900">{order.customerName}</h4>
                    <p className="text-sm text-gray-500">Order #{order.id}</p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center text-sm text-gray-600">
                      <Calendar className="w-4 h-4 mr-1" />
                      <span>Created: {new Date(order.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  {order.items.map((item) => (
                    <div key={item.id} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">{item.product.product}</span>
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">
                          {item.quantityFulfilled || 0} / {item.quantity} units
                        </span>
                        <div className="w-24 h-2 bg-gray-200 rounded-full">
                          <div 
                            className="h-2 bg-purple-600 rounded-full" 
                            style={{ 
                              width: `${((item.quantityFulfilled || 0) / item.quantity) * 100}%` 
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 pt-3 border-t">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-600">Total Progress</span>
                    <span className="font-medium">{calculateProgress(order)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-purple-600 h-2 rounded-full" 
                      style={{ width: `${calculateProgress(order)}%` }}
                    />
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No orders in fulfillment queue</p>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Delivery Overview</h3>
          <Link
            to="/dashboard/orders"
            className="text-purple-600 hover:text-purple-700 text-sm font-medium"
          >
            View All Orders
          </Link>
        </div>

        {loadingDeliveries ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
          </div>
        ) : deliveryError ? (
          <div className="text-center py-8 text-red-600">
            {deliveryError}
            <button
              onClick={loadDeliveryOrders}
              className="block mx-auto mt-2 text-sm text-purple-600 hover:text-purple-700"
            >
              Try Again
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-purple-600">Pending Deliveries</p>
                    <p className="text-2xl font-bold text-purple-900">{deliveryOrders.pendingDeliveries.length}</p>
                  </div>
                  <Truck className="w-8 h-8 text-purple-500" />
                </div>
              </div>

              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-600">Completed Today</p>
                    <p className="text-2xl font-bold text-green-900">{deliveryOrders.completedToday}</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-600">Scheduled Tomorrow</p>
                    <p className="text-2xl font-bold text-blue-900">{deliveryOrders.scheduledTomorrow}</p>
                  </div>
                  <Calendar className="w-8 h-8 text-blue-500" />
                </div>
              </div>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-6 py-3 border-b">
                <h4 className="font-medium text-gray-900">Today's Pending Deliveries</h4>
              </div>
              <div className="divide-y divide-gray-200">
                {deliveryOrders.pendingDeliveries.map((delivery) => (
                  <div key={delivery.id} className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h5 className="text-sm font-medium text-gray-900">{delivery.customerName}</h5>
                        <p className="text-sm text-gray-500">Order #{delivery.id}</p>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-sm text-gray-500">
                          <Clock className="w-4 h-4 inline-block mr-1" />
                          {new Date(delivery.createdAt).toLocaleTimeString()}
                        </div>
                        <Link
                          to={`/dashboard/orders/${delivery.id}`}
                          className="text-purple-600 hover:text-purple-700 text-sm font-medium"
                        >
                          View Details
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}

                {deliveryOrders.pendingDeliveries.length === 0 && (
                  <div className="px-6 py-8 text-center text-gray-500">
                    No pending deliveries for today
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DashboardHome;